import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, Loader2, Sparkles, Trash2, CheckCircle, FileText, Zap, UserCheck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Nuova pratica utenze partendo dalla bolletta: OCR locale della bolletta (dati utenza +
// intestatario) e dei documenti del cliente → cliente trovato o registrato → bene "utenze" in
// anagrafica → Pratica Utenze, con i documenti allegati in anagrafica. Usa solo API esistenti
// (/ocr/extract, /clients, /assets, /utility-practices).
const TIPI_DOC = [
  { value: 'carta_identita', label: "Carta d'identità", sigla: 'CIE', rango: 4 },
  { value: 'patente', label: 'Patente', sigla: 'PAT', rango: 4 },
  { value: 'passaporto', label: 'Passaporto', sigla: 'PASS', rango: 4 },
  { value: 'permesso_soggiorno', label: 'Permesso di soggiorno', sigla: 'PSE', rango: 4 },
  { value: 'tessera_sanitaria', label: 'Tessera sanitaria', sigla: 'TS', rango: 3 },
  { value: 'visura_camerale', label: 'Visura camerale', sigla: 'VISURA', rango: 2 },
];
// "rango" = affidabilità della fonte per i dati della persona (bolletta = 1)
const CAMPI_PERSONA = ['last_name', 'first_name', 'fiscal_code', 'gender', 'birth_date', 'birth_comune', 'birth_provincia',
  'birth_abroad', 'birth_country', 'address', 'address_number', 'city', 'city_provincia', 'postal_code'];
const CAMPI_AZIENDA = ['company_name', 'vat_number', 'pec', 'company_address', 'company_address_number', 'company_city',
  'company_provincia', 'company_postal_code'];
const CAMPI_UTENZA = ['tipo_utenza', 'pod_pdr_codice', 'codice_cliente_utente', 'fornitore', 'indirizzo_fornitura',
  'potenza_impegnata', 'classe_misuratore', 'matricola', 'consumo_annuo', 'iban_domiciliazione'];

const clienteVuoto = {
  is_company: false, last_name: '', first_name: '', fiscal_code: '', gender: '', birth_date: '', birth_comune: '',
  birth_provincia: '', birth_abroad: false, birth_country: '', address: '', address_number: '', city: '',
  city_provincia: '', postal_code: '', phone: '', email: '', company_name: '', vat_number: '', pec: '',
  company_address: '', company_address_number: '', company_city: '', company_provincia: '', company_postal_code: '',
};
const utenzaVuota = Object.fromEntries(CAMPI_UTENZA.map((k) => [k, k === 'tipo_utenza' ? 'luce' : '']));
const praticaVuota = { descrizione: '', fornitore_da: '', fornitore_a: '', costo_pratica: '', data_scadenza: '', notes: '' };

const maiuscolo = (v) => (typeof v === 'string' ? v.toUpperCase() : v);

// La bolletta riporta "VIA SAN QUIRICO, 264" e "POGGIO A CAIANO PO": civico e provincia separati
const separaIndirizzo = (d, via, civico, comune, provincia) => {
  const out = { ...d };
  const m = (out[via] || '').match(/^(.*?)[,\s]+(\d+[A-Za-z]?(?:\/[A-Za-z0-9]+)?)$/);
  if (m && !out[civico]) { out[via] = m[1].trim(); out[civico] = m[2]; }
  const p = (out[comune] || '').match(/^(.*?)\s*\(?\b([A-Za-z]{2})\)?$/);
  if (p && !out[provincia] && p[1].trim().split(' ').length >= 1 && p[1].trim().length > 2) { out[comune] = p[1].trim(); out[provincia] = p[2].toUpperCase(); }
  return out;
};

const NuovaDaBolletta = () => {
  const [bolletta, setBolletta] = useState(null); // { file }
  const [documenti, setDocumenti] = useState([]); // { file, tipo, sigla, scadenza }
  const [tipoDoc, setTipoDoc] = useState('carta_identita');
  const [leggendo, setLeggendo] = useState('');
  const [utenza, setUtenza] = useState(utenzaVuota);
  const [cliente, setCliente] = useState(clienteVuoto);
  const fonti = useRef({}); // campo → rango della fonte che l'ha compilato
  const [esistente, setEsistente] = useState(null);
  const [pratica, setPratica] = useState(praticaVuota);
  const [fornitori, setFornitori] = useState([]);
  const [creando, setCreando] = useState(false);
  const [fatto, setFatto] = useState(null);

  useEffect(() => {
    axios.get(`${API}/utility-practices/fornitori`).then((r) => setFornitori(r.data || [])).catch(() => {});
  }, []);

  // Unisce i dati letti: un campo si sovrascrive solo con una fonte più affidabile
  const unisci = (dati, rangoPersona, rangoAzienda) => {
    setCliente((prev) => {
      const nuovo = { ...prev };
      const nuoveFonti = fonti.current;
      const applica = (campi, rango) => campi.forEach((k) => {
        const v = dati[k];
        if (v === undefined || v === null || v === '') return;
        if ((nuoveFonti[k] || 0) > rango && prev[k]) return;
        nuovo[k] = typeof v === 'boolean' ? v : maiuscolo(String(v));
        nuoveFonti[k] = rango;
      });
      applica(CAMPI_PERSONA, rangoPersona);
      applica(CAMPI_AZIENDA, rangoAzienda);
      if (dati.is_company || dati.company_name || dati.vat_number) nuovo.is_company = true;
      if (nuovo.birth_abroad) { nuovo.birth_comune = ''; nuovo.birth_provincia = ''; }
      return nuovo;
    });
  };

  const ocr = async (file, tipo) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('document_type', tipo);
    const res = await axios.post(`${API}/ocr/extract`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.extracted_data || {};
  };

  const caricaBolletta = async (file) => {
    if (!file) return;
    setBolletta({ file });
    setLeggendo('bolletta');
    try {
      const d = await ocr(file, 'fattura_utenza');
      setUtenza((u) => {
        const n = { ...u };
        CAMPI_UTENZA.forEach((k) => { if (d[k]) n[k] = k === 'tipo_utenza' ? String(d[k]).toLowerCase() : maiuscolo(String(d[k])); });
        return n;
      });
      setPratica((p) => ({ ...p, fornitore_da: p.fornitore_da || maiuscolo(d.fornitore || '') }));
      let dati = separaIndirizzo(d, 'address', 'address_number', 'city', 'city_provincia');
      dati = separaIndirizzo(dati, 'company_address', 'company_address_number', 'company_city', 'company_provincia');
      unisci(dati, 1, 1);
      toast.success('Dati letti dalla bolletta: controllali');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lettura della bolletta non riuscita: compila a mano');
    } finally {
      setLeggendo('');
    }
  };

  const caricaDocumento = async (file) => {
    if (!file) return;
    const tipo = TIPI_DOC.find((t) => t.value === tipoDoc);
    setLeggendo('documento');
    try {
      const d = await ocr(file, tipo.value);
      const dati = { ...d };
      if (tipo.value === 'visura_camerale') {
        dati.is_company = true;
      } else {
        // documenti di persona: luogo di nascita ricavato dal CF se disponibile
        if (d.cf_birth_abroad !== undefined) {
          dati.birth_abroad = !!d.cf_birth_abroad;
          dati.birth_comune = d.cf_birth_comune || d.birth_place || '';
          dati.birth_provincia = d.cf_birth_provincia || d.birth_province || '';
          dati.birth_country = d.cf_birth_country || '';
        } else {
          dati.birth_comune = d.birth_place || '';
          dati.birth_provincia = d.birth_province || '';
        }
      }
      unisci(dati, tipo.rango, tipo.value === 'visura_camerale' ? 3 : 0);
      setDocumenti((prev) => [...prev.filter((x) => x.sigla !== tipo.sigla), { file, tipo: tipo.value, sigla: tipo.sigla, label: tipo.label, scadenza: d.expiry_date || '' }]);
      toast.success(`Dati letti da: ${tipo.label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lettura del documento non riuscita');
      setDocumenti((prev) => [...prev, { file, tipo: tipo.value, sigla: tipo.sigla, label: tipo.label, scadenza: '' }]);
    } finally {
      setLeggendo('');
    }
  };

  // Cliente già in anagrafica? (privato: codice fiscale; azienda: P.IVA)
  useEffect(() => {
    const chiave = cliente.is_company ? cliente.vat_number : cliente.fiscal_code;
    if (!chiave || chiave.length < 11) { setEsistente(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/clients`, { params: { page: 1, page_size: 10, search: chiave } });
        const trovato = (r.data.items || []).find((c) => (cliente.is_company
          ? (c.is_company && (c.vat_number || '').replace(/\D/g, '') === chiave.replace(/\D/g, ''))
          : (!c.is_company && (c.fiscal_code || '').toUpperCase() === chiave.toUpperCase())));
        setEsistente(trovato || null);
      } catch (_) { setEsistente(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [cliente.is_company, cliente.vat_number, cliente.fiscal_code]);

  // CF mancante (es. passaporto): calcolato dai dati anagrafici
  const calcolaCf = async () => {
    const c = cliente;
    if (!c.last_name || !c.first_name || !c.gender || !c.birth_date || (!c.birth_abroad && (!c.birth_comune || !c.birth_provincia)) || (c.birth_abroad && !c.birth_country)) {
      toast.error('Per calcolare il CF servono cognome, nome, sesso, data e luogo di nascita');
      return;
    }
    try {
      const r = await axios.post(`${API}/calculate-fiscal-code`, {
        surname: c.last_name, name: c.first_name, birth_date: c.birth_date, gender: c.gender,
        birth_abroad: c.birth_abroad, birth_comune: c.birth_comune, birth_provincia: c.birth_provincia, birth_country: c.birth_country,
      });
      setCliente((x) => ({ ...x, fiscal_code: r.data.fiscal_code }));
    } catch (_) { toast.error('Calcolo CF non riuscito'); }
  };

  const allega = async (clientId, file, descrizione, categoria, scadenza) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('description', descrizione.replace(/\s+/g, ' ').trim().toUpperCase());
    fd.append('category', categoria);
    if (scadenza) fd.append('expiry_date', scadenza);
    await axios.post(`${API}/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const crea = async () => {
    if (!utenza.pod_pdr_codice) { toast.error('Manca il POD/PDR (o codice utenza)'); return; }
    if (!esistente && (cliente.is_company ? !cliente.company_name : !cliente.last_name)) {
      toast.error(cliente.is_company ? 'Manca la ragione sociale' : 'Manca il cognome del cliente');
      return;
    }
    setCreando(true);
    const passi = [];
    try {
      // 1) cliente
      let c = esistente;
      if (!c) {
        const r = await axios.post(`${API}/clients`, { ...cliente, consents: { operativo: true, marketing: false, profilazione: false } });
        c = r.data;
        passi.push(`Cliente registrato: ${c.company_name || `${c.last_name} ${c.first_name}`}`);
      } else {
        passi.push(`Cliente esistente: ${c.company_name || `${c.last_name} ${c.first_name}`}`);
      }
      // 2) utenza (riusata se lo stesso POD/PDR è già tra i beni del cliente)
      const beni = await axios.get(`${API}/assets`, { params: { client_id: c.id } });
      let bene = (beni.data || []).find((b) => b.asset_type === 'utenze' && (b.pod_pdr_codice || '').toUpperCase() === utenza.pod_pdr_codice.toUpperCase());
      if (!bene) {
        const r = await axios.post(`${API}/assets`, { client_id: c.id, asset_type: 'utenze', ...utenza });
        bene = r.data;
        passi.push(`Utenza aggiunta: ${utenza.tipo_utenza.toUpperCase()} ${utenza.pod_pdr_codice}`);
      } else {
        passi.push(`Utenza già presente: ${utenza.pod_pdr_codice}`);
      }
      // 3) pratica utenze
      const nomeCliente = c.company_name || `${c.last_name} ${c.first_name}`;
      const r = await axios.post(`${API}/utility-practices`, {
        client_id: c.id,
        asset_id: bene.id,
        tipo_utenza: utenza.tipo_utenza,
        descrizione: pratica.descrizione || `${utenza.tipo_utenza.toUpperCase()} ${utenza.pod_pdr_codice}`,
        stato: 'in_corso',
        costo_pratica: parseFloat(String(pratica.costo_pratica).replace(',', '.')) || 0,
        fornitore_da: pratica.fornitore_da,
        fornitore_a: pratica.fornitore_a,
        data_scadenza: pratica.data_scadenza,
        notes: pratica.notes,
      });
      passi.push('Pratica utenze creata');
      // 4) documenti in anagrafica: bolletta (gestione) e documenti del cliente (anagrafici)
      let allegati = 0;
      try {
        if (bolletta) {
          await allega(c.id, bolletta.file, `BOLLETTA ${utenza.tipo_utenza} ${utenza.pod_pdr_codice} ${nomeCliente}`, 'gestione', '');
          allegati += 1;
        }
        for (const d of documenti) {
          await allega(c.id, d.file, `${d.sigla} ${d.sigla === 'VISURA' ? (c.company_name || '') : `${c.last_name || ''} ${c.first_name || ''}`}`, 'anagrafico', d.scadenza);
          allegati += 1;
        }
        if (allegati) passi.push(`${allegati} documenti allegati in anagrafica`);
      } catch (_) {
        passi.push('Attenzione: alcuni documenti non sono stati allegati');
      }
      setFatto({ passi, practiceId: r.data.id, cliente: nomeCliente });
      toast.success('Pratica creata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione');
      if (passi.length) setFatto({ passi: [...passi, 'Interrotto: completa a mano'], parziale: true });
    } finally {
      setCreando(false);
    }
  };

  const ricomincia = () => {
    setBolletta(null); setDocumenti([]); setUtenza(utenzaVuota); setCliente(clienteVuoto); fonti.current = {};
    setEsistente(null); setPratica(praticaVuota); setFatto(null);
  };

  const campo = (label, valore, onChange, props = {}) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input value={valore || ''} onChange={(e) => onChange(e.target.value)} {...props} />
    </div>
  );
  const setU = (k) => (v) => setUtenza((u) => ({ ...u, [k]: maiuscolo(v) }));
  const setC = (k) => (v) => setCliente((c) => ({ ...c, [k]: maiuscolo(v) }));

  if (fatto) {
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Nuova da bolletta</h1>
        <Card className={fatto.parziale ? 'border-amber-300' : 'border-emerald-300'}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2 text-lg font-semibold">
              {fatto.parziale ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <CheckCircle className="w-5 h-5 text-emerald-600" />}
              {fatto.parziale ? 'Completato in parte' : `Fatto: ${fatto.cliente}`}
            </div>
            <ul className="list-disc pl-6 text-sm text-slate-700 space-y-1">{fatto.passi.map((p, i) => <li key={i}>{p}</li>)}</ul>
            <div className="flex gap-2 pt-2">
              <Link to="/utilities"><Button><Zap className="w-4 h-4 mr-2" />Vai a Pratiche Utenze</Button></Link>
              <Button variant="outline" onClick={ricomincia}>Nuova pratica da bolletta</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="nuova-da-bolletta-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Nuova da bolletta</h1>
        <p className="text-slate-600 mt-1">Carica la bolletta luce/gas e i documenti del cliente: cliente, utenza e pratica si compilano da soli</p>
      </div>

      {/* 1. Bolletta */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">1. Bolletta</h2>
            {bolletta && <span className="text-xs text-slate-500 flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{bolletta.file.name}</span>}
          </div>
          <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-yellow-300 bg-yellow-50 rounded-lg py-3 text-sm text-yellow-800 hover:bg-yellow-100 cursor-pointer ${leggendo ? 'opacity-60 pointer-events-none' : ''}`}>
            {leggendo === 'bolletta' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {leggendo === 'bolletta' ? 'Lettura della bolletta...' : (bolletta ? 'Carica un\'altra bolletta' : 'Carica foto o PDF della bolletta')}
            <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => { caricaBolletta(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Tipo utenza</Label>
              <Select value={utenza.tipo_utenza} onValueChange={(v) => setUtenza((u) => ({ ...u, tipo_utenza: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="luce">Luce</SelectItem>
                  <SelectItem value="gas">Gas</SelectItem>
                  <SelectItem value="acqua">Acqua</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {campo(utenza.tipo_utenza === 'luce' ? 'POD *' : utenza.tipo_utenza === 'gas' ? 'PDR *' : 'Codice utenza *', utenza.pod_pdr_codice, setU('pod_pdr_codice'), { className: 'font-mono' })}
            {campo('Fornitore attuale', utenza.fornitore, (v) => { setU('fornitore')(v); setPratica((p) => ({ ...p, fornitore_da: maiuscolo(v) })); })}
            {campo('Codice cliente', utenza.codice_cliente_utente, setU('codice_cliente_utente'))}
            <div className="md:col-span-2">{campo('Indirizzo di fornitura', utenza.indirizzo_fornitura, setU('indirizzo_fornitura'))}</div>
            {utenza.tipo_utenza === 'luce' && campo('Potenza impegnata (kW)', utenza.potenza_impegnata, setU('potenza_impegnata'))}
            {utenza.tipo_utenza === 'gas' && campo('Classe misuratore', utenza.classe_misuratore, setU('classe_misuratore'))}
            {campo('Matricola contatore', utenza.matricola, setU('matricola'))}
            {campo('Consumo annuo', utenza.consumo_annuo, setU('consumo_annuo'))}
            <div className="md:col-span-2">{campo('IBAN domiciliazione', utenza.iban_domiciliazione, setU('iban_domiciliazione'), { className: 'font-mono' })}</div>
          </div>
        </CardContent>
      </Card>

      {/* 2. Documenti */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <h2 className="font-semibold text-slate-900">2. Documenti del cliente <span className="text-sm font-normal text-slate-500">(facoltativi, anche più di uno)</span></h2>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 flex flex-col sm:flex-row gap-2">
            <Select value={tipoDoc} onValueChange={setTipoDoc}>
              <SelectTrigger className="bg-white sm:w-60"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPI_DOC.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <label className={`flex-1 flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${leggendo ? 'opacity-60 pointer-events-none' : ''}`}>
              {leggendo === 'documento' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {leggendo === 'documento' ? 'Lettura in corso...' : `Carica ${TIPI_DOC.find((t) => t.value === tipoDoc).label}`}
              <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => { caricaDocumento(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
          </div>
          {documenti.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {documenti.map((d, i) => (
                <span key={i} className="inline-flex items-center gap-2 text-sm bg-slate-100 rounded-full px-3 py-1">
                  <b>{d.sigla}</b> {d.file.name.slice(0, 30)}
                  <button type="button" onClick={() => setDocumenti((prev) => prev.filter((_, j) => j !== i))} title="Togli"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3. Cliente */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">3. Cliente</h2>
            <div className="flex items-center gap-2 text-sm">
              <span className={!cliente.is_company ? 'font-medium' : 'text-slate-400'}>Privato</span>
              <Switch checked={cliente.is_company} onCheckedChange={(v) => setCliente((c) => ({ ...c, is_company: v }))} />
              <span className={cliente.is_company ? 'font-medium' : 'text-slate-400'}>Azienda</span>
            </div>
          </div>
          {esistente && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm">
              <UserCheck className="w-4 h-4" />
              Cliente già in anagrafica: <b>{esistente.company_name || `${esistente.last_name} ${esistente.first_name}`}</b> — verrà usato questo (i dati sotto non lo modificano).
            </div>
          )}
          {cliente.is_company && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">{campo('Ragione sociale *', cliente.company_name, setC('company_name'))}</div>
              {campo('P.IVA', cliente.vat_number, (v) => setCliente((c) => ({ ...c, vat_number: v.replace(/\D/g, '') })))}
              {campo('PEC', cliente.pec, (v) => setCliente((c) => ({ ...c, pec: v })))}
              <div className="md:col-span-2">{campo('Sede legale (via)', cliente.company_address, setC('company_address'))}</div>
              {campo('Civico', cliente.company_address_number, setC('company_address_number'))}
              {campo('CAP', cliente.company_postal_code, setC('company_postal_code'))}
              {campo('Comune', cliente.company_city, setC('company_city'))}
              {campo('Provincia', cliente.company_provincia, setC('company_provincia'), { maxLength: 2 })}
            </div>
          )}
          {cliente.is_company && <p className="text-sm font-medium text-blue-700">Referente / legale rappresentante</p>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {campo(cliente.is_company ? 'Cognome' : 'Cognome *', cliente.last_name, setC('last_name'))}
            {campo('Nome', cliente.first_name, setC('first_name'))}
            <div className="space-y-1">
              <Label className="text-xs">Codice fiscale</Label>
              <div className="flex gap-1">
                <Input value={cliente.fiscal_code} onChange={(e) => setC('fiscal_code')(e.target.value)} className="font-mono" />
                <Button type="button" variant="outline" size="sm" onClick={calcolaCf} title="Calcola dai dati anagrafici">Calcola</Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sesso</Label>
              <Select value={cliente.gender || ''} onValueChange={(v) => setCliente((c) => ({ ...c, gender: v }))}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent><SelectItem value="M">M</SelectItem><SelectItem value="F">F</SelectItem></SelectContent>
              </Select>
            </div>
            {campo('Data di nascita', cliente.birth_date, (v) => setCliente((c) => ({ ...c, birth_date: v })), { type: 'date' })}
            {cliente.birth_abroad
              ? campo('Nazione di nascita', cliente.birth_country, setC('birth_country'))
              : campo('Comune di nascita', cliente.birth_comune, setC('birth_comune'))}
            {!cliente.birth_abroad && campo('Prov. nascita', cliente.birth_provincia, setC('birth_provincia'), { maxLength: 2 })}
            <div className="flex items-end gap-2 pb-2 text-sm">
              <Switch checked={cliente.birth_abroad} onCheckedChange={(v) => setCliente((c) => ({ ...c, birth_abroad: v }))} /> Nato all'estero
            </div>
            <div className="md:col-span-2">{campo('Indirizzo di residenza', cliente.address, setC('address'))}</div>
            {campo('Civico', cliente.address_number, setC('address_number'))}
            {campo('CAP', cliente.postal_code, setC('postal_code'))}
            {campo('Comune', cliente.city, setC('city'))}
            {campo('Provincia', cliente.city_provincia, setC('city_provincia'), { maxLength: 2 })}
            {campo('Telefono', cliente.phone, (v) => setCliente((c) => ({ ...c, phone: v })))}
            {campo('Email', cliente.email, (v) => setCliente((c) => ({ ...c, email: v.toLowerCase() })), { type: 'email' })}
          </div>
        </CardContent>
      </Card>

      {/* 4. Pratica */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <h2 className="font-semibold text-slate-900">4. Pratica utenze</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">{campo('Descrizione', pratica.descrizione, (v) => setPratica((p) => ({ ...p, descrizione: maiuscolo(v) })), { placeholder: `es. CAMBIO FORNITORE ${utenza.tipo_utenza.toUpperCase()}` })}</div>
            {campo('Da fornitore', pratica.fornitore_da, (v) => setPratica((p) => ({ ...p, fornitore_da: maiuscolo(v) })))}
            <div className="space-y-1">
              <Label className="text-xs">A fornitore</Label>
              <Input list="fornitori-lista" value={pratica.fornitore_a} onChange={(e) => setPratica((p) => ({ ...p, fornitore_a: maiuscolo(e.target.value) }))} />
              <datalist id="fornitori-lista">{fornitori.map((f) => <option key={f} value={f} />)}</datalist>
            </div>
            {campo('Costo pratica (€)', pratica.costo_pratica, (v) => setPratica((p) => ({ ...p, costo_pratica: v.replace(/[^\d.,]/g, '') })), { inputMode: 'decimal' })}
            {campo('Scadenza', pratica.data_scadenza, (v) => setPratica((p) => ({ ...p, data_scadenza: v })), { type: 'date' })}
            <div className="md:col-span-2">{campo('Note', pratica.notes, (v) => setPratica((p) => ({ ...p, notes: v })))}</div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={ricomincia} disabled={creando}>Svuota</Button>
            <Button onClick={crea} disabled={creando || !!leggendo}>
              {creando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creazione...</> : 'Crea cliente, utenza e pratica'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NuovaDaBolletta;
